import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ujmsSentencingCatalog, ujmsEnterSentence, ujmsSealCase, ujmsSealDocument, ujmsSealDocket, ujmsRedactDocket, ujmsV2SentencingStructure } from "../../services/ujmsApi";
import { inputCls, selectCls, Field, btnPrimary, btnGhost, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Scale, Plus, X, BookOpen } from "lucide-react";

const blank = () => ({ _k: Math.random().toString(36).slice(2), type: "INCARCERATION", length_value: "", length_unit: "MONTHS", amount: "", hours: "", notes: "" });
const MONTHS = { DAYS: 1 / 30, MONTHS: 1, YEARS: 12 };
const toMonths = (v, u) => Number(v || 0) * (MONTHS[u] || 1);

/** Statutory envelope for the counts that will stand convicted (class as amended) — mirrors sentencing_rules.check_components. */
export function envelopeFor(counts) {
  const known = counts.filter((c) => c.rule);
  if (!known.length) return null;
  const top = known.reduce((a, b) => (b.rule.incarceration[1] > a.rule.incarceration[1] ? b : a));
  return { max_inc: top.rule.incarceration[1], min_inc: top.rule.incarceration[0], controlling: top, custodial: top.rule.custodial, jail_days: Math.max(...known.map((c) => c.rule.jail_days_max || 0)),
    prob_allowed: known.every((c) => c.rule.probation_max), prob_max: Math.min(...known.map((c) => c.rule.probation_max || 0)), fine_max: Math.max(...known.map((c) => c.rule.fine_max)),
    not_probationable: known.filter((c) => !c.rule.probation_max) };
}

export function violationsFor(components, env) {
  if (!env) return [];
  const errs = [];
  const hasProb = components.some((c) => ["PROBATION", "CONDITIONAL_DISCHARGE"].includes(c.type));
  for (const c of components) {
    const m = toMonths(c.length_value, c.length_unit);
    if (c.type === "INCARCERATION" && c.length_value) {
      if (env.max_inc === 0) errs.push(`${env.controlling.class} is fine-only — no incarceration`);
      else if (m > env.max_inc + 1e-6) errs.push(`Incarceration exceeds the ${env.controlling.class} maximum of ${env.max_inc} months`);
      else if (m + 1e-6 < env.min_inc && !hasProb) errs.push(`Incarceration is below the ${env.controlling.class} minimum of ${env.min_inc} months`);
    }
    if (["PROBATION", "CONDITIONAL_DISCHARGE"].includes(c.type)) {
      if (!env.prob_allowed) errs.push(`${c.type.replace("_", " ").toLowerCase()} is not available — ${env.not_probationable.map((x) => `Count ${x.count_number} (${x.class})`).join(", ")} is not probationable`);
      else if (c.length_value && m > env.prob_max + 1e-6) errs.push(`${c.type.replace("_", " ").toLowerCase()} exceeds the ${env.prob_max}-month maximum`);
    }
    if (c.type === "FINE" && Number(c.amount || 0) > env.fine_max) errs.push(`Fine exceeds the $${env.fine_max.toLocaleString()} statutory maximum`);
  }
  if (!components.some((c) => c.type === "INCARCERATION") && env.min_inc > 0 && !env.prob_allowed && components.length) errs.push(`${env.controlling.class} requires at least ${env.min_inc} months imprisonment — probation is not available`);
  return [...new Set(errs)];
}

export function StatutePanel({ structure, activeIds, env, testId = "sentence-statute-panel" }) {
  if (!structure) return null;
  return (
    <div className="border border-ujms-tab bg-[#f3f6fb] p-1.5 text-[11px]" data-testid={testId}>
      <div className="flex items-center gap-1 font-bold text-ujms-deep uppercase text-[10px]"><BookOpen className="w-3 h-3" /> Statutory sentencing structure — the statute (as amended) fixes the sentence</div>
      <table className="w-full mt-1">
        <tbody>
          {structure.counts.map((c) => {
            const active = activeIds.includes(c.charge_id);
            return <tr key={c.charge_id} className={cls("align-top", !active && "text-slate-400")} data-testid={`sentence-statute-${c.charge_id}`}>
              <td className="pr-1 font-mono whitespace-nowrap">Ct {c.count_number}</td>
              <td className="pr-1 font-mono whitespace-nowrap">{c.statute_code}</td>
              <td className="pr-1"><b className={active ? "text-slate-900" : ""}>{c.class || "unclassified"}</b>{c.amended && <span className="ml-1 text-[9px] font-bold uppercase text-amber-700 border border-amber-400 px-0.5">as amended</span>}<span className="ml-1 text-[10px] uppercase">{c.status}</span></td>
              <td className="text-slate-600">{c.summary?.replace(/^[^:]+:\s*/, "")}</td>
            </tr>;
          })}
        </tbody>
      </table>
      {env ? <div className="mt-1 text-[10.5px] text-slate-800" data-testid="sentence-envelope">Controlling class <b>{env.controlling.class}</b> (Count {env.controlling.count_number}): {env.max_inc ? `${env.min_inc}–${env.max_inc} months ${env.custodial === "IDOC" ? "IDOC" : "jail"}` : "no incarceration"} · {env.prob_allowed ? `probation / conditional discharge ≤ ${env.prob_max} months` : <span className="text-red-700 font-semibold">NOT probationable</span>} · fine ≤ ${env.fine_max.toLocaleString()}</div>
        : <div className="mt-1 text-[10.5px] text-amber-800">Select the count(s) to convict — the envelope follows the convicted counts.</div>}
    </div>
  );
}

export function SentenceForm({ caseId, charges = [], draftOnly = false, onDone, onCancel, cancelLabel = "Cancel", testPrefix = "sentence" }) {
  const [catalog, setCatalog] = useState(null);
  const [structure, setStructure] = useState(null);
  const [components, setComponents] = useState([blank()]);
  const [meta, setMeta] = useState({ credit_days: 0, run: "CONCURRENT", notes: "" });
  const [convictIds, setConvictIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { ujmsSentencingCatalog().then((r) => setCatalog(r.data)).catch(() => {}); ujmsV2SentencingStructure(caseId).then((r) => setStructure(r.data)).catch(() => setStructure(null)); }, [caseId]);

  const convictedCount = charges.filter((c) => c.status === "CONVICTED").length;
  const openCharges = charges.filter((c) => !["CONVICTED", "DISMISSED", "ACQUITTED", "MERGED"].includes(c.status));
  const needsConviction = charges.length > 0 && convictedCount === 0;
  const activeIds = useMemo(() => [...(structure?.convicted_count_ids || []), ...convictIds], [structure, convictIds]);
  const env = useMemo(() => (structure ? envelopeFor(structure.counts.filter((c) => activeIds.includes(c.charge_id))) : null), [structure, activeIds]);
  const violations = useMemo(() => violationsFor(components, env), [components, env]);

  const setC = (i, patch) => setComponents((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const disabledType = (t) => {
    if (!env) return null;
    if (t === "INCARCERATION" && env.max_inc === 0) return `${env.controlling.class} carries no incarceration`;
    if (["PROBATION", "CONDITIONAL_DISCHARGE"].includes(t) && !env.prob_allowed) return "Not probationable under the statute for the convicted count(s)";
    return null;
  };
  const maxFor = (c) => { if (!env) return undefined; const unit = MONTHS[c.length_unit] || 1; if (c.type === "INCARCERATION") return Math.floor(env.max_inc / unit); if (["PROBATION", "CONDITIONAL_DISCHARGE"].includes(c.type)) return Math.floor(env.prob_max / unit); return undefined; };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsEnterSentence(caseId, {
        components: components.map((c) => ({
          type: c.type,
          length_value: c.length_value ? Number(c.length_value) : null,
          length_unit: c.length_unit || null,
          amount: c.amount ? Number(c.amount) : null,
          hours: c.hours ? Number(c.hours) : null,
          notes: c.notes,
        })),
        credit_days: Number(meta.credit_days) || 0, run: meta.run, notes: meta.notes,
        convict_charge_ids: convictIds, draft_only: draftOnly,
      });
      toast.success(draftOnly ? "Sentence entered — sentencing order drafted for your signature" : "Sentence entered — sentencing order signed and docketed");
      if (convictIds.length) toast.success(`${convictIds.length} count(s) convicted — finding of guilty docketed`);
      if (r.data.documents?.length) toast.success(`${r.data.documents.map((d) => d.title.split(" — ")[0]).join(", ")} generated`);
      onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Sentencing failed"); }
    finally { setBusy(false); }
  };

  const needsLength = (t) => ["INCARCERATION", "PROBATION", "CONDITIONAL_DISCHARGE", "LICENSE_SUSPENSION"].includes(t);
  const needsAmount = (t) => ["FINE", "RESTITUTION"].includes(t);

  return (
    <>
      <div className="space-y-3" data-testid={`${testPrefix}-form`}>
        <StatutePanel structure={structure} activeIds={activeIds} env={env} />
        {needsConviction && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-2.5 space-y-1.5" data-testid="sentence-conviction-panel">
            <div className="text-xs font-semibold text-amber-800">
              No count of conviction on this case yet — select the count(s) to enter a finding of GUILTY with this sentence:
            </div>
            {openCharges.map((ch) => (
              <label key={ch.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={convictIds.includes(ch.id)}
                  onChange={(e) => setConvictIds((ids) => e.target.checked ? [...ids, ch.id] : ids.filter((x) => x !== ch.id))}
                  data-testid={`sentence-convict-${ch.id}`} />
                <span className="font-mono text-xs">Ct {ch.count_number}</span> {ch.statute_code} — {ch.description}
                <span className="text-xs text-slate-400">({ch.classification})</span>
              </label>
            ))}
            {!openCharges.length && <div className="text-xs text-red-600">All counts are already disposed (dismissed/acquitted) — sentencing is not available.</div>}
          </div>
        )}
        <div className="space-y-2">
          {components.map((c, i) => (
            <div key={c._k} className="flex items-end gap-2 border border-slate-200 rounded-sm p-2" data-testid={`sentence-component-${i}`}>
              <Field label="Component" className="flex-1">
                <select className={selectCls} value={c.type} onChange={(e) => setC(i, { type: e.target.value })} data-testid={`sc-type-${i}`}>
                  {Object.entries(catalog?.component_types || {}).map(([k, v]) => <option key={k} value={k} disabled={!!disabledType(k)} title={disabledType(k) || undefined}>{v}{disabledType(k) ? " — not available under the statute" : ""}</option>)}
                </select>
              </Field>
              {needsLength(c.type) && (
                <>
                  <Field label={`Length${maxFor(c) != null ? ` (≤ ${maxFor(c)})` : ""}`}><input type="number" min="1" max={maxFor(c)} className={cls(inputCls, "w-20", env && maxFor(c) != null && Number(c.length_value) > maxFor(c) && "!border-red-500 bg-red-50")} value={c.length_value} onChange={(e) => setC(i, { length_value: e.target.value })} data-testid={`sc-length-${i}`} /></Field>
                  <Field label="Unit">
                    <select className={selectCls} value={c.length_unit} onChange={(e) => setC(i, { length_unit: e.target.value })} data-testid={`sc-unit-${i}`}>
                      {(catalog?.length_units || []).map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </Field>
                </>
              )}
              {needsAmount(c.type) && (
                <Field label={`Amount ($)${c.type === "FINE" && env ? ` (≤ ${env.fine_max.toLocaleString()})` : ""}`}><input type="number" min="0" step="0.01" max={c.type === "FINE" && env ? env.fine_max : undefined} className={cls(inputCls, "w-28", c.type === "FINE" && env && Number(c.amount) > env.fine_max && "!border-red-500 bg-red-50")} value={c.amount} onChange={(e) => setC(i, { amount: e.target.value })} data-testid={`sc-amount-${i}`} /></Field>
              )}
              {c.type === "COMMUNITY_SERVICE" && (
                <Field label="Hours"><input type="number" min="1" className={`${inputCls} w-20`} value={c.hours} onChange={(e) => setC(i, { hours: e.target.value })} data-testid={`sc-hours-${i}`} /></Field>
              )}
              <Field label="Notes" className="flex-1"><input className={inputCls} value={c.notes} onChange={(e) => setC(i, { notes: e.target.value })} data-testid={`sc-notes-${i}`} /></Field>
              {components.length > 1 && (
                <button className="text-red-600 pb-2" onClick={() => setComponents(components.filter((_, j) => j !== i))} data-testid={`sc-remove-${i}`}><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          <button className={btnGhost} onClick={() => setComponents([...components, blank()])} data-testid="sentence-add-component"><Plus className="w-3.5 h-3.5" /> Add Component</button>
        </div>
        {violations.length > 0 && <ul className="border border-red-300 bg-red-50 text-red-800 text-[11px] px-2 py-1 list-disc pl-5" data-testid="sentence-violations">{violations.map((v) => <li key={v}>{v}</li>)}</ul>}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Credit for time served (days)">
            <input type="number" min="0" className={inputCls} value={meta.credit_days} onChange={(e) => setMeta({ ...meta, credit_days: e.target.value })} data-testid="sentence-credit-input" />
          </Field>
          <Field label="Components run">
            <select className={selectCls} value={meta.run} onChange={(e) => setMeta({ ...meta, run: e.target.value })} data-testid="sentence-run-select">
              <option value="CONCURRENT">CONCURRENT</option>
              <option value="CONSECUTIVE">CONSECUTIVE</option>
            </select>
          </Field>
          <Field label="Additional terms"><input className={inputCls} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} data-testid="sentence-notes-input" /></Field>
        </div>
        <div className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5">
          {draftOnly
            ? "Entering the sentence drafts a Sentencing Order (Cook County format) for your signature and posts a certified SENTENCE_ENTERED docket entry."
            : "Entering the sentence signs and enters a Sentencing Order automatically (with PDF) and posts a certified SENTENCE_ENTERED docket entry."}
        </div>
      </div>
      <DialogFooter>
        <button className={btnGhost} onClick={onCancel} data-testid={`${testPrefix}-cancel-btn`}>{cancelLabel}</button>
        <button className={btnPrimary} onClick={submit} disabled={busy || (needsConviction && convictIds.length === 0) || violations.length > 0}
          title={needsConviction && convictIds.length === 0 ? "Select at least one count to convict first" : violations.length ? "Sentence is outside the statutory structure" : undefined}
          data-testid="sentence-submit-btn">{busy ? "Entering…" : draftOnly ? "Enter Sentence & Draft Order" : "Sign & Enter Sentence"}</button>
      </DialogFooter>
    </>
  );
}

export function SentenceDialog({ open, onClose, caseId, caseNumber, onDone, charges = [] }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sentence-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5" /> Enter Sentence — {caseNumber}</DialogTitle></DialogHeader>
        {open && <SentenceForm caseId={caseId} charges={charges} onDone={onDone} onCancel={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

export function SealDialog({ target, onClose, onDone }) {
  // target: {kind: 'case'|'document'|'docket', id, label, sealed}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      const fn = { case: ujmsSealCase, document: ujmsSealDocument, docket: ujmsSealDocket }[target.kind];
      await fn(target.id, { seal: !target.sealed, reason });
      toast.success(`${target.kind === "case" ? "Case" : target.kind === "document" ? "Document" : "Docket entry"} ${target.sealed ? "unsealed" : "SEALED"}`);
      setReason(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="seal-dialog">
        <DialogHeader><DialogTitle>{target?.sealed ? "Unseal" : "Seal"} — {target?.label}</DialogTitle></DialogHeader>
        <Field label="Reason / authorizing order" required>
          <textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="seal-reason-input" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="seal-submit-btn">{target?.sealed ? "Unseal" : "Seal"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RedactDialog({ entry, onClose, onDone }) {
  const [f, setF] = useState({ redacted_text: "", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (entry) setF({ redacted_text: entry.description, reason: "" }); }, [entry]);
  const submit = async () => {
    if (!f.redacted_text.trim() || !f.reason.trim()) { toast.error("Redacted text and reason are required"); return; }
    setBusy(true);
    try { await ujmsRedactDocket(entry.id, f); toast.success("Docket entry redacted — original preserved for authorized users"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="redact-dialog">
        <DialogHeader><DialogTitle>Redact Docket Entry #{entry?.sequence_number}</DialogTitle></DialogHeader>
        <Field label="Redacted public text" required>
          <textarea className={inputCls} rows={3} value={f.redacted_text} onChange={(e) => setF({ ...f, redacted_text: e.target.value })} data-testid="redact-text-input" />
        </Field>
        <Field label="Reason / authorizing order" required>
          <input className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="redact-reason-input" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="redact-submit-btn">Apply Redaction</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
